import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { useKatpackStore } from "../stores/useKatpackStore";
import { WalletContext } from "../contexts/WalletContext";
import { useChainConfig } from "../hooks/useChainConfig";
import { ethers } from "ethers";
import {
  detectProvider,
  getEthersProvider,
  getSigner,
  clearProviderCache,
} from "../utils/providerDetection";

async function storeJwtTokens(setJwtToken, setRefreshToken, accessToken, refreshToken, timeoutMs = 5000) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(new Error("JWT storage timed out"));
    }, timeoutMs);

    const unsubscribe = useKatpackStore.subscribe(
      (state) => state.instance.jwtToken,
      (newToken) => {
        if (newToken === accessToken) {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(true);
        }
      }
    );

    try {
      setJwtToken(accessToken);
      if (refreshToken) setRefreshToken(refreshToken);

      const current = useKatpackStore.getState().instance.jwtToken;
      if (current === accessToken) {
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(true);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      unsubscribe();
      reject(error);
    }
  });
}

export const SignedWalletProvider = ({ children, config }) => {
  const { chainConfig } = useChainConfig(config?.apiBaseUrl);

  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [account, setAccount] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [network, setNetwork] = useState(null);

  const walletActionMutexRef = useRef(false);
  const hasAttemptedRestoreRef = useRef(false);

  const jwtToken = useKatpackStore((state) => state.instance.jwtToken);
  const setJwtToken = useKatpackStore((state) => state.setJwtToken);
  const setRefreshToken = useKatpackStore((state) => state.setRefreshToken);
  const clearAuthTokens = useKatpackStore((state) => state.clearAuthTokens);
  const setApiBaseUrl = useKatpackStore((state) => state.setApiBaseUrl);
  const userSub = useKatpackStore((state) => state.instance.userSub);
  const setUserSub = useKatpackStore((state) => state.setUserSub);
  const clearUserSub = useKatpackStore((state) => state.clearUserSub);
  const clearKatbotSession = useKatpackStore((state) => state.clearKatbotSession);

  useEffect(() => {
    setApiBaseUrl(config?.apiBaseUrl || null);
  }, [config?.apiBaseUrl, setApiBaseUrl]);

  const logout = useCallback(async () => {
    try {
      const ethereum = window.ethereum;
      if (ethereum?.request) {
        await ethereum.request({
          method: "wallet_revokePermissions",
          params: [{ eth_accounts: {} }],
        });
      }
    } catch {
      // Non-fatal: older MetaMask versions may not support this
    }

    setProvider(null);
    setSigner(null);
    setAccount(null);
    setIsConnected(false);
    setConnectionStatus("disconnected");
    setIsInitializing(false);
    clearAuthTokens?.();
    clearUserSub?.();
    clearKatbotSession?.();
    setError(null);
    setNetwork(null);
    walletActionMutexRef.current = false;
    hasAttemptedRestoreRef.current = false;
    clearProviderCache();
  }, [clearAuthTokens, clearUserSub, clearKatbotSession]);

  // JWT expiration check — attempts refresh before logging out
  useEffect(() => {
    const checkTokenExpiration = async () => {
      if (!jwtToken) return;
      try {
        const decoded = jwtDecode(jwtToken);
        const now = Date.now() / 1000;

        if (decoded.exp && decoded.exp < now + 30) {
          const refreshToken = useKatpackStore.getState().instance?.refreshToken;
          if (!refreshToken) { logout(); return; }

          try {
            const resp = await fetch(`${config?.apiBaseUrl}/auth/refresh`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ refresh_token: refreshToken }),
            });
            if (resp.ok) {
              const { access_token, refresh_token } = await resp.json();
              await storeJwtTokens(setJwtToken, setRefreshToken, access_token, refresh_token);
            } else {
              logout();
            }
          } catch {
            logout();
          }
          return;
        }

        const timeUntilExpiry = (decoded.exp - now - 30) * 1000;
        if (timeUntilExpiry > 0) {
          const timer = setTimeout(checkTokenExpiration, Math.min(timeUntilExpiry, 60000));
          return () => clearTimeout(timer);
        }
      } catch {
        logout();
      }
    };

    return () => { checkTokenExpiration(); };
  }, [jwtToken, logout, config?.apiBaseUrl, setJwtToken, setRefreshToken]);

  // Restore wallet from JWT on page load
  useEffect(() => {
    const restoreWalletState = async () => {
      if (hasAttemptedRestoreRef.current) {
        setIsInitializing(false);
        return;
      }
      if (walletActionMutexRef.current) return;

      walletActionMutexRef.current = true;
      hasAttemptedRestoreRef.current = true;
      setIsInitializing(true);

      try {
        if (!jwtToken || account || provider) return;

        const ethereum = await detectProvider({ mustBeMetaMask: true, timeout: 3000 });
        if (!ethereum) return;

        const decoded = jwtDecode(jwtToken);
        const tokenWallet = decoded.sub;
        if (!tokenWallet) return;

        const accounts = await ethereum.request({ method: "eth_accounts" });
        if (!accounts?.length) return;

        if (accounts[0].toLowerCase() !== tokenWallet.toLowerCase()) {
          clearAuthTokens?.();
          return;
        }

        const { ethersProvider } = await getEthersProvider();
        const ethersSigner = await getSigner(tokenWallet);
        const accountAddress = await ethersSigner.getAddress();
        const networkInfo = await ethersProvider.getNetwork();

        if (accountAddress.toLowerCase() !== tokenWallet.toLowerCase()) return;

        setProvider(ethersProvider);
        setSigner(ethersSigner);
        setAccount(accountAddress);
        setNetwork(networkInfo);
        setIsConnected(true);
        setConnectionStatus("connected");

        if (decoded.sub !== userSub) setUserSub(decoded.sub);
      } catch (err) {
        // Provider error during restore — don't wipe tokens, just leave disconnected
        // so the user can manually reconnect without losing their session
        console.warn("[Auth] Wallet restore failed:", err?.message);
      } finally {
        setIsInitializing(false);
        walletActionMutexRef.current = false;
      }
    };

    restoreWalletState();
  }, [jwtToken, account, provider, userSub, setUserSub, clearAuthTokens]);

  useEffect(() => {
    if (!jwtToken && isInitializing && !walletActionMutexRef.current) {
      setIsInitializing(false);
    }
  }, [jwtToken, isInitializing]);

  const isTestnetNetwork = useCallback(() => {
    if (!network) return false;
    return Number(network.chainId) === chainConfig.testnet_chain_id;
  }, [network, chainConfig.testnet_chain_id]);

  const login = useCallback(async () => {
    if (walletActionMutexRef.current || isLoading) return false;

    walletActionMutexRef.current = true;
    setIsLoading(true);
    setIsInitializing(false);
    setError(null);
    setConnectionStatus("connecting");

    const ethereum = await detectProvider({ mustBeMetaMask: true, timeout: 3000 });
    if (!ethereum) {
      setError("MetaMask is not installed or not detected.");
      setConnectionStatus("error");
      setIsLoading(false);
      walletActionMutexRef.current = false;
      return false;
    }

    try {
        console.log("[Login] Starting MetaMask connection...");
        
        // Request account access - MetaMask popup will appear
        console.log("[Login] Calling eth_requestAccounts...");
        
        // Check if already connected
        let accounts = await ethereum.request({ method: "eth_accounts" });
        
        if (!accounts?.length) {
          // Not connected yet — show the MetaMask connect dialog
          accounts = await ethereum.request({ method: "eth_requestAccounts" });
        }
        
        console.log("[Login] ✓ accounts:", accounts);
        
        if (!accounts?.length) {
          throw new Error("No accounts returned from MetaMask.");
        }
        
        const accountAddress = accounts[0];
        console.log("[Login] ✓ Connected account:", accountAddress);
        
        // Clear cache BEFORE creating provider
        clearProviderCache();
        console.log("[Login] ✓ Cleared provider cache");
        
        const { ethersProvider, ethers } = await getEthersProvider();
        console.log("[Login] ✓ Got ethersProvider");
        
        const normalizedAddress = ethers.utils.getAddress(accountAddress);
        
        // Get signer with explicit account address
        const ethersSigner = ethersProvider.getSigner(normalizedAddress);
        console.log("[Login] ✓ Got signer");
        
        // Verify signer has account
        const signerAddress = await ethersSigner.getAddress();
        console.log("[Login] ✓ Signer address:", signerAddress);
        
        const networkInfo = await ethersProvider.getNetwork();
        console.log("[Login] ✓ Network:", networkInfo.chainId, networkInfo.name);

        // Update wallet state so NavBar reflects connected status immediately
        setNetwork(networkInfo);
        setProvider(ethersProvider);
        setSigner(ethersSigner);
        setAccount(normalizedAddress);
        setIsConnected(true);
        setConnectionStatus("connected");

        // Nonce-based auth with KatPack API
        const nonceResp = await fetch(
          `${config.apiBaseUrl}/auth/nonce/${accountAddress}?chain_id=${networkInfo.chainId}`
        );
        if (!nonceResp.ok) {
          let detail = `Failed to get nonce (${nonceResp.status})`;
          try { const body = await nonceResp.json(); detail = body.detail || detail; } catch { /* noop */ }
          throw new Error(detail);
        }
        const { message } = await nonceResp.json();

        const signature = await ethersSigner.signMessage(message);

        const loginResp = await fetch(`${config.apiBaseUrl}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ address: accountAddress, signature, chain_id: networkInfo.chainId }),
        });

        if (!loginResp.ok) {
          let detail = `Login failed (${loginResp.status})`;
          try { const body = await loginResp.json(); detail = body.detail || detail; } catch { /* noop */ }
          throw new Error(detail);
        }
        const { access_token, refresh_token } = await loginResp.json();

        await storeJwtTokens(setJwtToken, setRefreshToken, access_token, refresh_token);

        const decoded = jwtDecode(access_token);
        if (decoded.sub !== userSub) setUserSub(decoded.sub);

        window.dispatchEvent(new CustomEvent("loginSuccess", { detail: { shouldNavigateToDashboard: true } }));

        setIsLoading(false);
        walletActionMutexRef.current = false;
        return true;
      } catch (err) {
        console.error("Login failed:", err);
        setError(err.message || "An error occurred during login.");
        setConnectionStatus("error");
        setIsLoading(false);
        walletActionMutexRef.current = false;
        return false;
      }
    }, [config?.apiBaseUrl, isLoading, setJwtToken, setRefreshToken, setUserSub, userSub]);

  const handleAccountsChanged = useCallback(
    async (newAccounts) => {
      // Only react if we were already connected — ignore the initial connect event
      if (!isConnected) return;
      if (!newAccounts.length) { logout(); return; }
      // Account switched while connected — re-auth required
      logout();
    },
    [logout, isConnected]
  );

  const handleChainChanged = useCallback(() => window.location.reload(), []);

  useEffect(() => {
    if (!window.ethereum) return;
    window.ethereum.on("accountsChanged", handleAccountsChanged);
    window.ethereum.on("chainChanged", handleChainChanged);
    return () => {
      window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
      window.ethereum.removeListener("chainChanged", handleChainChanged);
    };
  }, [handleAccountsChanged, handleChainChanged]);

  useEffect(() => {
    const handleSessionExpired = ({ detail }) => {
      console.log("[Auth] Session expired:", detail?.reason);
      logout();
      window.dispatchEvent(
        new CustomEvent("showToast", {
          detail: { type: "warning", message: "Your session has expired. Please log in again." },
        })
      );
    };
    window.addEventListener("sessionExpired", handleSessionExpired);
    return () => window.removeEventListener("sessionExpired", handleSessionExpired);
  }, [logout]);

  const value = useMemo(
    () => ({
      provider,
      signer,
      account,
      network,
      jwtToken,
      isLoading,
      error,
      isConnected,
      connectionStatus,
      isInitializing,
      isTestnetNetwork,
      login,
      logout,
    }),
    [provider, signer, account, network, jwtToken, isLoading, error, isConnected, connectionStatus, isInitializing, isTestnetNetwork, login, logout]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

SignedWalletProvider.displayName = "SignedWalletProvider";
